import { useTranslation } from "react-i18next";
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import defaultCommonContent from "../../../../content/common";
import { useActionReportMenuOptions } from "../../hooks/useActionReportMenuOptions";
import { IconCaret } from "../../../../common/icons/IconCaret/IconCaret";
import { Report } from "../../../../common/interfaces/config";
import { getCanonicalTitle } from "../../../../common/util/Incentives/IncentiveRegistry";
import "./ActionReportTitleSelector.scss";

type OptionType = {
  id: string;
  label: string;
  value?: string;
  selected?: boolean;
};

export const ActionReportTitleSelector = () => {
  const { t } = useTranslation(["actionReports", "navigation", "common"]);
  const disclaimerSymbol = t("disclaimerSymbol", defaultCommonContent["disclaimerSymbol"], { ns: "common" });
  const navigate = useNavigate();
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const { actionReportMenuOptions } = useActionReportMenuOptions() || { actionReportMenuOptions: [] };
  const stableOptionsRef = useRef<OptionType[]>([]);

  useEffect(() => {
    const matchingReport = actionReportMenuOptions?.find((report: Report) => location.pathname.includes(report.url));

    if (matchingReport && matchingReport.id !== selectedId) {
      setSelectedId(matchingReport.id);
    }
  }, [actionReportMenuOptions, location.pathname, selectedId]);

  const newOptions: OptionType[] = useMemo(
    () =>
      actionReportMenuOptions?.map(({ id, url, contentKey }: Report & { contentKey?: string }) => {
        const label = getCanonicalTitle({ id, contentKey: contentKey ?? id }, t, { symbol: disclaimerSymbol });
        return {
          id,
          value: url,
          selected: id === selectedId,
          label,
        };
      }) || [],
    [actionReportMenuOptions, selectedId, t, disclaimerSymbol],
  );

  // Only update options reference if the content actually changed
  const options = useMemo(() => {
    if (newOptions.length === 0 && stableOptionsRef.current.length === 0) {
      return newOptions;
    }

    if (newOptions.length !== stableOptionsRef.current.length) {
      stableOptionsRef.current = newOptions;
      return newOptions;
    }

    // Check if any option changed
    const hasChanged = newOptions.some((newOpt, idx) => {
      const oldOpt = stableOptionsRef.current[idx];
      return !oldOpt || newOpt.id !== oldOpt.id || newOpt.selected !== oldOpt.selected || newOpt.label !== oldOpt.label;
    });

    if (hasChanged) {
      stableOptionsRef.current = newOptions;
      return newOptions;
    }

    return stableOptionsRef.current;
  }, [newOptions]);

  const handleSelect = useCallback(
    (option: OptionType) => {
      setSelectedId(option.id);
      setIsOpen(false);

      if (option.value) {
        navigate(option.value);
      }
    },
    [navigate],
  );

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;
    const handleOutsideClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen]);

  // Don't render if no options are available
  if (!options.length) {
    return null;
  }

  const selectedOption = options.find((opt) => opt.selected);

  return (
    <div className="ActionReportTitleSelector" ref={containerRef}>
      <button
        type="button"
        className={`leaf-selector__dropdown-toggle leaf-selector__dropdown-toggle--blue${
          isOpen ? " leaf-selector__dropdown-toggle--open" : ""
        }`}
        onClick={handleToggle}
        aria-label="action-report-selector"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        data-testid="action-report-selector-toggle"
      >
        <div className="leaf-selector__text leaf-selector__text--padding" style={{ pointerEvents: "none" }}>
          <span className="leaf-selector__label">{selectedOption?.label}</span>
        </div>
        <div className="leaf-selector__suffix-icon leaf-selector__suffix-icon--blue" style={{ pointerEvents: "none" }}>
          <IconCaret direction={isOpen ? "up" : "down"} size="md" color="#2c2c2c" />
        </div>
      </button>

      {isOpen && (
        <div className="base-Popup-root base--open base-Select-popup leaf-selector__dropdown-menu leaf-selector__dropdown-menu--blue">
          <div className="leaf-selector__dropdown-options leaf-selector__dropdown-options--blue" role="menu">
            {options.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`leaf-selector__dropdown-item${option.selected ? " leaf-selector__dropdown-item--selected" : ""}`}
                onClick={() => handleSelect(option)}
                role="menuitem"
                data-testid={`action-report-menu-item-${option.id}`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
